import { 
    setMaxPages, 
    setAcceptedCount, 
    setPendingCount, 
    setBlockedCount,
    setTotalCount,
    toggleCheckedItem,
    setSelectedOrganizer,
    setEditedOrganizer,
    toggleEditModal,
    toggleNeedRefresh,
} from '../../store/organizersSlice';
import ApiService from '../../components/api_services/apiService.jsx';
import ActionMenu from '../../components/components/action_menu/ActionMenu.jsx';
import { ReactComponent as StatusUp } from '../../components/icons/status-up.svg';
import { ReactComponent as Edit } from '../../components/icons/edit.svg';
import { ReactComponent as TickCircle } from '../../components/icons/tick-circle.svg';
import { ReactComponent as CloseCircle } from '../../components/icons/close-circle.svg';
import { ReactComponent as Trash } from '../../components/icons/trash.svg';
import {
    ADMIN_PROFILES_PAGE_SIZE,
    filterOrganizersForTab,
    normalizeProfilesToOrganizers,
    sortRowsLocally,
} from '../profiles/profilesAdapter.js';

const DEFAULT_ORGANIZERS_PAGE_SIZE = ADMIN_PROFILES_PAGE_SIZE;
const ORGANIZER_FILTER_KEYS = {
    ALL: 'ALL',
    ACTIVE: 'ACTIVE',
    NOT_CONFIRMED: 'NOTCONFIRMED',
    PENDING: 'PENDING',
};

const ORGANIZER_STATUS_META = {
    [ORGANIZER_FILTER_KEYS.ALL]: { label: 'Wszyscy', toneClass: 'organizer-status--all' },
    [ORGANIZER_FILTER_KEYS.ACTIVE]: { label: 'Aktywny', toneClass: 'organizer-status--active' },
    [ORGANIZER_FILTER_KEYS.NOT_CONFIRMED]: { label: 'Niepotwierdzony', toneClass: 'organizer-status--inactive' },
    [ORGANIZER_FILTER_KEYS.PENDING]: { label: 'Oczekujący', toneClass: 'organizer-status--pending' },
};

const extractOrganizerProfilesEntries = (response) => {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.data)) return response.data;
    if (Array.isArray(response?.content)) return response.content;
    if (Array.isArray(response?.items)) return response.items;
    if (Array.isArray(response?.rows)) return response.rows;
    if (Array.isArray(response?.accountsWrapperResponseDtoList)) return response.accountsWrapperResponseDtoList;
    if (Array.isArray(response?.organiserProfiles)) return response.organiserProfiles;
    if (Array.isArray(response?.organizerProfiles)) return response.organizerProfiles;
    return [];
};

const extractOrganizerTotalCount = (response) => {
    const candidates = [
        response?.totalCount,
        response?.totalElements,
        response?.recordsCount,
        response?.count,
    ];

    const parsed = candidates
        .map((value) => Number(value))
        .find((value) => Number.isFinite(value) && value >= 0);

    return parsed ?? null;
};

const extractOrganizerTotalPages = (response) => {
    const candidates = [
        response?.maxPage,
        response?.numberOfPages,
        response?.totalPages,
        response?.pagesCount,
    ];

    const parsed = candidates
        .map((value) => Number(value))
        .find((value) => Number.isFinite(value) && value >= 0);

    return parsed ?? null;
};

const resolveOrganizerStatus = (profile) => {
    if (typeof profile?.isActivated === 'boolean') {
        if (profile.isActivated === false) return 'pending';
        return profile?.isEmailConfirmed === true ? 'confirmed' : 'unconfirmed';
    }

    const explicitStatus = String(profile?.status || '').toLowerCase();
    if (explicitStatus === 'accepted') return 'confirmed';
    if (explicitStatus === 'pending') return 'pending';
    if (explicitStatus === 'blocked') return 'unconfirmed';

    if (profile?.isApprovedByAdmin === false || profile?.isActivated === false) {
        return 'pending';
    }
    if (profile?.isBlocked === true || profile?.hasBlocked === true) {
        return 'unconfirmed';
    }

    // DTO z /api/organiser/profiles* bez pól statusowych traktujemy jako oczekujący.
    if (
        profile?.companyName !== undefined &&
        profile?.nip !== undefined &&
        profile?.street !== undefined &&
        profile?.city !== undefined &&
        profile?.postalCode !== undefined &&
        profile?.contactEmail !== undefined &&
        profile?.contactPhone !== undefined
    ) {
        return 'pending';
    }

    return 'confirmed';
};

const resolveAddress = (profile) => {
    if (profile?.address) return profile.address;

    const street = profile?.street || profile?.addressLine || '';
    const postalCode = profile?.postalCode || profile?.zipCode || '';
    const city = profile?.city || '';
    const line2 = [postalCode, city].filter(Boolean).join(' ').trim();

    if (street && line2) return `${street}, ${line2}`;
    if (street) return street;
    if (line2) return line2;
    return '-';
};

const normalizeOrganizerFromProfile = (profile = {}) => {
    const status = resolveOrganizerStatus(profile);
    return {
        id: profile.id ?? profile.organiserProfileId ?? profile.organizerProfileId ?? profile.userId,
        registrationDate: profile.registrationDate ?? profile.createdAt ?? profile.added_on_utc ?? '',
        companyName: profile.companyName ?? profile.name ?? '-',
        nip: profile.nip ?? '-',
        address: resolveAddress(profile),
        email: profile.email ?? profile.contactEmail ?? '-',
        phone: profile.phone ?? profile.contactPhone ?? '-',
        eventsCount: profile.eventsCount ?? '-',
        status,
        pendingType: profile.pendingType || (status === 'pending' ? 'new_profile' : ''),
        isBlocked: profile.isBlocked === true || profile.hasBlocked === true,
        description: profile.description || '',
        facebookUrl: profile.facebookUrl || profile.socialLink || '',
        websiteUrl: profile.websiteUrl || profile.siteLink || '',
        changedFields: profile.changedFields || {},
        isActive: profile.isActive ?? profile.isActivated ?? status !== 'pending',
        isApprovedByAdmin: profile.isApprovedByAdmin ?? (status !== 'pending'),
        _rawProfileEntry: profile,
    };
};

export const normalizeOrganizerProfile = (profile = {}) => {
    if (profile?.userResponseFullDto || profile?.organiserProfileResponseDto) {
        const [normalized] = normalizeProfilesToOrganizers([profile]);
        return normalized || null;
    }

    return normalizeOrganizerFromProfile(profile);
};

export const resolveOrganizerId = (organizer) => {
    if (!organizer) return null;
    return (
        organizer.id ??
        organizer.organiserProfileId ??
        organizer.organizerProfileId ??
        organizer.userId ??
        organizer?._rawProfileEntry?.organiserProfileResponseDto?.id ??
        organizer?._rawProfileEntry?.organizerProfileResponseDto?.id ??
        organizer?._rawProfileEntry?.userResponseFullDto?.id ??
        null
    );
};

export const isOrganizerPending = (organizer) => {
    if (!organizer) return false;
    return resolveOrganizerStatus(organizer) === 'pending';
};

// Fetch organizers data
export const fetchOrganizers = async (
    page, 
    sortBy, 
    sortDirection, 
    keyword, 
    _pageTab, 
    filters,
    dispatch, 
    setRawOrganizers,
    requestOptions = {}
) => {
    try {
        const query = {
            pageNumber: page,
            pageSize: DEFAULT_ORGANIZERS_PAGE_SIZE,
            filterQuery: resolveOrganizerFilterQuery(filters?.filterQuery),
            ...(keyword ? { searchQuery: keyword } : {}),
            ...(sortBy && sortDirection ? { sortBy, sortDirection } : {}),
        };

        if (requestOptions?.signal?.aborted) {
            return [];
        }

        const response = await ApiService.getOrganiserProfilesPaginated(query, requestOptions);
        const backendEntries = extractOrganizerProfilesEntries(response);

        const normalizedOrganizers = backendEntries.map((entry) => normalizeOrganizerProfile(entry)).filter(Boolean);
        const tabFilteredOrganizers = filterOrganizersForTab(normalizedOrganizers, 'all', filters || {});
        const sortedOrganizers = sortRowsLocally(tabFilteredOrganizers, sortBy, sortDirection);

        const backendTotalCount = extractOrganizerTotalCount(response);
        const backendTotalPages = extractOrganizerTotalPages(response);
        const hasBackendPaging = backendTotalCount !== null || backendTotalPages !== null;

        const totalCount = hasBackendPaging ? (backendTotalCount ?? sortedOrganizers.length) : sortedOrganizers.length;
        const totalPages = hasBackendPaging
            ? (backendTotalPages !== null
                ? Math.max(1, backendTotalPages)
                : Math.max(1, Math.ceil(totalCount / DEFAULT_ORGANIZERS_PAGE_SIZE)))
            : Math.max(1, Math.ceil(totalCount / DEFAULT_ORGANIZERS_PAGE_SIZE));

        const rowsForPage = hasBackendPaging
            ? sortedOrganizers
            : sortedOrganizers.slice(
                (page - 1) * DEFAULT_ORGANIZERS_PAGE_SIZE,
                page * DEFAULT_ORGANIZERS_PAGE_SIZE
            );

        dispatch(setAcceptedCount(0));
        dispatch(setPendingCount(0));
        dispatch(setBlockedCount(0));
        dispatch(setTotalCount(totalCount));
        dispatch(setMaxPages(totalPages));
        setRawOrganizers(rowsForPage);

        return rowsForPage;
    } catch (error) {
        if (error?.code === 'ERR_CANCELED') {
            return [];
        }
        console.error('Error fetching organizers from /api/organiser/profiles/paginated:', error);
        dispatch(setAcceptedCount(0));
        dispatch(setPendingCount(0));
        dispatch(setBlockedCount(0));
        dispatch(setTotalCount(0));
        dispatch(setMaxPages(Math.max(1, page)));
        setRawOrganizers([]);
        return [];
    }
};

// Map organizers to table rows
export const mapOrganizersToRows = (organizers, checkedItems, dispatch) => {
    const openOrganizerDetails = async (organizer) => {
        const organizerId = resolveOrganizerId(organizer);
        if (!organizerId) return;

        try {
            const response = await ApiService.getOrganiserProfile(organizerId);
            const profile = normalizeOrganizerProfile(response?.data ?? response);
            dispatch(setSelectedOrganizer(profile || organizer));
        } catch (error) {
            console.error('Blad pobierania szczegolow organizatora:', error);
            dispatch(setSelectedOrganizer(organizer));
        }
    };

    const openOrganizerEdit = async (organizer) => {
        const organizerId = resolveOrganizerId(organizer);
        if (!organizerId) return;

        try {
            const response = await ApiService.getOrganiserProfile(organizerId);
            const profile = normalizeOrganizerProfile(response?.data ?? response);
            dispatch(setEditedOrganizer(profile || organizer));
        } catch (error) {
            console.error('Blad pobierania danych organizatora do edycji:', error);
            dispatch(setEditedOrganizer(organizer));
        }

        dispatch(toggleEditModal());
    };

    const showInProgressMessage = () => {
        alert('W trakcie implementacji.');
    };

    return organizers.map((org) => {
        const isChecked = checkedItems.includes(org.id);
        const isPending = isOrganizerPending(org);

        const actionItems = [
            {
                key: 'details',
                label: 'Szczegoly',
                icon: StatusUp,
                onClick: () => openOrganizerDetails(org),
            },
            {
                key: 'edit',
                label: 'Edytuj',
                icon: Edit,
                onClick: () => openOrganizerEdit(org),
            },
        ];

        if (isPending) {
            actionItems.push(
                {
                    key: 'approve',
                    label: 'Zaakceptuj',
                    icon: TickCircle,
                    onClick: async () => {
                        try {
                            const organizerId = resolveOrganizerId(org);
                            if (!organizerId) return;
                            await ApiService.approveOrganiser(organizerId);
                            await ApiService.getOrganiserProfile(organizerId);
                            dispatch(toggleNeedRefresh());
                        } catch (error) {
                            console.error('Blad akceptacji organizatora:', error);
                        }
                    },
                },
                {
                    key: 'reject',
                    label: 'Odrzuc',
                    icon: CloseCircle,
                    danger: true,
                    onClick: showInProgressMessage,
                }
            );
        } else {
            actionItems.push({
                key: 'delete',
                label: 'Usun',
                icon: Trash,
                danger: true,
                onClick: showInProgressMessage,
            });
        }

        const commonColumns = [
            <label
                key={`checkbox-${org.id}`}
                className="checkbox-wrapper"
                onClick={(e) => e.stopPropagation()}
            >
                <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => dispatch(toggleCheckedItem(org.id))}
                />
            </label>,
            <ActionMenu key={`actions-${org.id}`} items={actionItems} />,
            formatDate(org.registrationDate),
            <span
                key={`company-${org.id}`}
                className="company-name-link"
                onClick={() => dispatch(setSelectedOrganizer(org))}
            >
                {org.companyName}
            </span>,
            org.nip,
            <span key={`address-${org.id}`} className="address-cell">
                {org.address}
            </span>,
            <span key={`contact-${org.id}`} className="contact-cell">
                {org.email}
            </span>,
        ];

        return [
            ...commonColumns,
            <span key={`status-${org.id}`} className={`organizer-status ${resolveOrganizerStatusClassName(org)}`}>
                {resolveOrganizerStatusLabel(org)}
            </span>,
        ];
    });
};

// Helper function to format date
const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('pl-PL', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
    }).replace(/\//g, '-');
};

const resolveOrganizerStatusFilterKey = (org) => {
    const status = resolveOrganizerStatus(org);
    if (status === 'pending') return 'PENDING';
    if (status === 'unconfirmed') return 'NOTACTIVE';
    return 'ACTIVE';
};

const resolveOrganizerStatusLabelByFilterKey = (statusKey) => {
    const normalized = String(statusKey || 'ALL').toUpperCase();
    if (normalized === 'PENDING') return 'Oczekujący';
    if (normalized === 'NOTCONFIRMED') return 'Niepotwierdzony';
    if (normalized === 'ACTIVE') return 'Potwierdzony';
    return 'Wszyscy';
};

const resolveOrganizerStatusLabel = (org) => {
    return resolveOrganizerStatusLabelByFilterKey(resolveOrganizerStatusFilterKey(org));
};

export const resolveOrganizerToneClass = (statusKey) => {
    const normalized = String(statusKey || 'ALL').toUpperCase();
    if (normalized === 'ALL') return 'organizer-status--all';
    if (normalized === 'PENDING') return 'organizer-status--pending';
    if (normalized === 'ACTIVE') return 'organizer-status--active';
    return 'organizer-status--inactive';
};

const resolveOrganizerStatusClassName = (org) => {
    return resolveOrganizerToneClass(resolveOrganizerStatusFilterKey(org));
};

const resolveOrganizerFilterQuery = (filterQuery) => {
    const normalized = String(filterQuery || 'ALL').toUpperCase();
    if (['ALL', 'ACTIVE', 'NOTCONFIRMED', 'PENDING'].includes(normalized)) {
        return normalized;
    }
    return 'ALL';
};
